import type { ReactNode } from 'react';
import { comicCategories, regions, statuses, sortOptions } from '@/mocks/comics';
import type { FilterState } from '@/utils/comic';

type ComicFilterProps = {
  value: FilterState;
  onChange: (next: FilterState) => void;
  showCategory?: boolean;
};

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-b border-background-200 py-4 last:border-0">
      <p className="mb-3 text-sm font-medium text-foreground-900">{title}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Pill({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition cursor-pointer ${
        active
          ? 'bg-primary-500 font-medium text-background-50'
          : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
      }`}
    >
      {children}
    </button>
  );
}

export default function ComicFilter({ value, onChange, showCategory = true }: ComicFilterProps) {
  const toggleCategory = (id: string) => {
    const next = value.categories.includes(id)
      ? value.categories.filter((item) => item !== id)
      : [...value.categories, id];
    onChange({ ...value, categories: next });
  };

  return (
    <div>
      <FilterGroup title="地区">
        {regions.map((region) => (
          <Pill key={region} active={value.region === region} onClick={() => onChange({ ...value, region })}>
            {region}
          </Pill>
        ))}
      </FilterGroup>

      {showCategory && (
        <FilterGroup title="题材">
          {comicCategories.map((category) => (
            <Pill
              key={category.id}
              active={value.categories.includes(category.id)}
              onClick={() => toggleCategory(category.id)}
            >
              {category.name}
            </Pill>
          ))}
        </FilterGroup>
      )}

      <FilterGroup title="状态">
        {statuses.map((status) => (
          <Pill key={status} active={value.status === status} onClick={() => onChange({ ...value, status })}>
            {status}
          </Pill>
        ))}
      </FilterGroup>

      <FilterGroup title="排序">
        {sortOptions.map((sort) => (
          <Pill key={sort} active={value.sort === sort} onClick={() => onChange({ ...value, sort })}>
            {sort}
          </Pill>
        ))}
      </FilterGroup>
    </div>
  );
}